/**
 * `<HelpdeskMembersPage>` — `/w/:slug/helpdesk/members`
 *
 * The desk roster a `helpdesk_admin` manages (helpdesk-team unit). Scoped by
 * construction: the backend `/helpdesk/members` + `/helpdesk/invitations`
 * endpoints only ever surface members holding a helpdesk role, and only accept
 * helpdesk roles on invite / re-assign. A workspace admin sees it too.
 *
 * Gated at the route/nav on `manage-members Helpdesk`, so every action here is
 * already authorized — no per-action `<Can>` needed.
 */

import { useAbility, useWorkspace, type PublicMember } from "@cometchat-ai/auth/client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableBody,
  TableHeader,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import {
  Headset,
  Loader2,
  Mail,
  MoreHorizontal,
  Plus,
  Trash2,
  Users,
  X,
} from "../components/icons";
import { ListSkeleton } from "../components/list-skeleton";
import { PageShell } from "../components/page-shell";
import { RolePill } from "../components/settings-parts";
import { SupportAgentDialog } from "../components/support-agent-dialog";
import { agentProfileKeys, listAgentProfiles } from "../handoff/api";
import { formatApiError } from "../lib/format-error";
import {
  changeHelpdeskMemberRole,
  helpdeskRoleLabel,
  helpdeskTeamKeys,
  inviteHelpdeskMember,
  listHelpdeskInvitations,
  listHelpdeskMembers,
  listHelpdeskRoles,
  removeHelpdeskMember,
  revokeHelpdeskInvitation,
  type HelpdeskInvitation,
  type HelpdeskMember,
  type HelpdeskRoleList,
} from "./team-api";

function memberName(m: HelpdeskMember): string | null {
  const name = `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim();
  return name.length > 0 ? name : null;
}
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

/** Role options for the invite + re-assign controls: built-in helpdesk keys
 * first, then the desk's custom roles (value = RoleDefinition.id). */
function useRoleOptions(roles: HelpdeskRoleList | undefined) {
  return useMemo(() => {
    const builtin = (roles?.builtin ?? []).map((r) => ({
      value: r.name,
      label: helpdeskRoleLabel(r.name),
    }));
    const custom = (roles?.custom ?? []).map((r) => ({ value: r.id, label: r.name }));
    return [...builtin, ...custom];
  }, [roles]);
}

export function HelpdeskMembersPage() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const qc = useQueryClient();
  const ability = useAbility();
  // Every operator can VIEW the roster (`read-members`); only an admin
  // (`manage-members`) sees the invite / re-assign / remove affordances and
  // the pending-invitations section.
  const canManage = ability.can("manage-members", "Helpdesk");
  // Operational profile (display name / avatar / availability / capacity /
  // skills) rides the handoff-configure ability — same gate as the old
  // Support Agents page this row action replaces.
  const canConfigure = ability.can("configure", "HandoffEvent");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<HelpdeskMember | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<HelpdeskInvitation | null>(null);
  const [profileTarget, setProfileTarget] = useState<HelpdeskMember | null>(null);

  const profilesQuery = useQuery({
    queryKey: agentProfileKeys.list(slug),
    queryFn: () => listAgentProfiles(),
    enabled: canConfigure,
  });
  const profiles = profilesQuery.data?.items ?? [];

  const [membersQuery, invitesQuery, rolesQuery] = useQueries({
    queries: [
      { queryKey: helpdeskTeamKeys.members(slug), queryFn: () => listHelpdeskMembers(slug) },
      {
        queryKey: helpdeskTeamKeys.invitations(slug),
        queryFn: () => listHelpdeskInvitations(slug),
        enabled: canManage,
      },
      {
        queryKey: helpdeskTeamKeys.roles(slug),
        queryFn: () => listHelpdeskRoles(slug),
        staleTime: 30_000,
      },
    ],
  });

  const members = membersQuery.data ?? [];
  const invitations = invitesQuery.data ?? [];
  const roleOptions = useRoleOptions(rolesQuery.data);
  const customNames = useMemo(
    () => new Map((rolesQuery.data?.custom ?? []).map((c) => [c.id, c.name] as const)),
    [rolesQuery.data],
  );

  const changeRole = useMutation({
    mutationFn: (v: { memberId: string; role: string }) =>
      changeHelpdeskMemberRole(slug, v.memberId, v.role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: helpdeskTeamKeys.members(slug) });
      toast.success(t("helpdeskTeam.members.roleChanged"));
    },
    onError: (err) =>
      toast.error(t("helpdeskTeam.members.roleChangeFailed"), { description: formatApiError(err) }),
  });

  return (
    <PageShell
      className="p-0"
      data-testid="helpdesk-members"
      description={t("helpdeskTeam.members.subtitle")}
      actions={
        canManage ? (
          <Button
            onClick={() => setInviteOpen(true)}
            size="sm"
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            data-testid="helpdesk-members-invite"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t("helpdeskTeam.members.invite")}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col py-2">
        {invitations.length > 0 ? (
          <div className="pb-4" data-testid="helpdesk-members-pending">
            <div className="flex items-center gap-2.5 px-6 pb-3 pt-1">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <h3 className="text-sm font-semibold text-foreground">
                {t("helpdeskTeam.members.pendingTitle", { count: invitations.length })}
              </h3>
            </div>
            <DataGrid className="border-t border-border">
              <TableHeader>
                <DataGridHeaderRow>
                  <DataGridHead className="pl-6">
                    {t("helpdeskTeam.members.table.email")}
                  </DataGridHead>
                  <DataGridHead className="w-[180px]">
                    {t("helpdeskTeam.members.table.role")}
                  </DataGridHead>
                  <DataGridHead className="w-[140px] pr-6">
                    {t("helpdeskTeam.members.table.expires")}
                  </DataGridHead>
                  <DataGridHead className="w-[90px] pr-6 text-right" divider={false}>
                    {t("helpdeskTeam.members.table.actions")}
                  </DataGridHead>
                </DataGridHeaderRow>
              </TableHeader>
              <TableBody>
                {invitations.map((inv) => (
                  <DataGridRow key={inv.id} data-testid={`helpdesk-invite-row-${inv.id}`}>
                    <DataGridCell className="truncate pl-6 text-foreground">
                      {inv.email}
                    </DataGridCell>
                    <DataGridCell>
                      <RolePill role={inv.role} label={helpdeskRoleLabel(inv.role, customNames)} />
                    </DataGridCell>
                    <DataGridCell className="whitespace-nowrap pr-6">
                      {fmtDate(inv.expiresAt)}
                    </DataGridCell>
                    <DataGridCell className="pr-6 text-right" divider={false}>
                      <RowMenu
                        label={t("helpdeskTeam.members.table.actions")}
                        onRemove={() => setRevokeTarget(inv)}
                        removeLabel={t("helpdeskTeam.members.revoke.trigger")}
                        testId={`helpdesk-invite-actions-${inv.id}`}
                      />
                    </DataGridCell>
                  </DataGridRow>
                ))}
              </TableBody>
            </DataGrid>
          </div>
        ) : null}

        <div className="flex items-center gap-2.5 px-6 pb-3 pt-1">
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-foreground">
            {t("helpdeskTeam.members.activeTitle", { count: members.length })}
          </h3>
        </div>
        {membersQuery.isPending ? (
          <ListSkeleton rows={3} itemClassName="h-10 w-full" className="space-y-2 px-6" />
        ) : membersQuery.error ? (
          <div className="px-6">
            <ErrorState message={membersQuery.error.message} onRetry={membersQuery.refetch} />
          </div>
        ) : members.length === 0 ? (
          <div className="px-6 py-12">
            <EmptyState
              data-testid="helpdesk-members-empty"
              icon={Users}
              title={t("helpdeskTeam.members.empty")}
            />
          </div>
        ) : (
          <DataGrid className="border-t border-border">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6">{t("helpdeskTeam.members.table.user")}</DataGridHead>
                <DataGridHead className="w-[200px]">
                  {t("helpdeskTeam.members.table.role")}
                </DataGridHead>
                <DataGridHead className="w-[140px] pr-6" divider={canManage}>
                  {t("helpdeskTeam.members.table.joined")}
                </DataGridHead>
                {canManage ? (
                  <DataGridHead className="w-[90px] pr-6 text-right" divider={false}>
                    {t("helpdeskTeam.members.table.actions")}
                  </DataGridHead>
                ) : null}
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const name = memberName(m);
                return (
                  <DataGridRow key={m.id} data-testid={`helpdesk-member-row-${m.id}`}>
                    <DataGridCell className="pl-6">
                      <div className="min-w-0">
                        {name ? (
                          <div className="truncate text-[13px] text-foreground">{name}</div>
                        ) : null}
                        <div className="truncate text-[13px] text-muted-foreground">{m.email}</div>
                      </div>
                    </DataGridCell>
                    <DataGridCell>
                      {canManage ? (
                        <Select
                          value={m.role}
                          onValueChange={(role) => changeRole.mutate({ memberId: m.id, role })}
                        >
                          <SelectTrigger
                            className="h-8 w-[180px] rounded-md text-[13px]"
                            data-testid={`helpdesk-member-role-${m.id}`}
                          >
                            <SelectValue>{helpdeskRoleLabel(m.role, customNames)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <RolePill role={m.role} label={helpdeskRoleLabel(m.role, customNames)} />
                      )}
                    </DataGridCell>
                    <DataGridCell className="whitespace-nowrap pr-6" divider={canManage}>
                      {fmtDate(m.joinedAt)}
                    </DataGridCell>
                    {canManage ? (
                      <DataGridCell className="pr-6 text-right" divider={false}>
                        <RowMenu
                          label={t("helpdeskTeam.members.table.actions")}
                          onRemove={() => setRemoveTarget(m)}
                          removeLabel={t("helpdeskTeam.members.remove.trigger")}
                          profileLabel={
                            canConfigure ? t("helpdeskTeam.members.profileAction") : undefined
                          }
                          onProfile={canConfigure ? () => setProfileTarget(m) : undefined}
                          testId={`helpdesk-member-actions-${m.id}`}
                        />
                      </DataGridCell>
                    ) : null}
                  </DataGridRow>
                );
              })}
            </TableBody>
          </DataGrid>
        )}
      </div>

      <InviteHelpdeskDialog
        slug={slug}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roleOptions={roleOptions}
      />
      {profileTarget ? (
        // Operational profile editor (moved here from the removed Support
        // Agents page). Existing profile → edit; none yet → create pinned to
        // this member (same lazy-provision idea the Teams page uses).
        <SupportAgentDialog
          workspaceSlug={slug}
          open
          onOpenChange={(o) => {
            if (!o) setProfileTarget(null);
          }}
          profile={profiles.find((p) => p.userId === profileTarget.userId) ?? null}
          assignableMembers={[
            {
              userId: profileTarget.userId,
              email: profileTarget.email,
              firstName: profileTarget.firstName,
              lastName: profileTarget.lastName,
            } as PublicMember,
          ]}
        />
      ) : null}
      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title={t("helpdeskTeam.members.remove.title")}
        description={t("helpdeskTeam.members.remove.description", {
          email: removeTarget?.email ?? "",
        })}
        confirmLabel={t("helpdeskTeam.members.remove.confirm")}
        mutationFn={() => removeHelpdeskMember(slug, removeTarget!.id)}
        invalidateKey={helpdeskTeamKeys.members(slug)}
        successMsg={t("helpdeskTeam.members.remove.done")}
      />
      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(o) => !o && setRevokeTarget(null)}
        title={t("helpdeskTeam.members.revoke.title")}
        description={t("helpdeskTeam.members.revoke.description", {
          email: revokeTarget?.email ?? "",
        })}
        confirmLabel={t("helpdeskTeam.members.revoke.confirm")}
        mutationFn={() => revokeHelpdeskInvitation(slug, revokeTarget!.id)}
        invalidateKey={helpdeskTeamKeys.invitations(slug)}
        successMsg={t("helpdeskTeam.members.revoke.done")}
      />
    </PageShell>
  );
}

function RowMenu({
  label,
  removeLabel,
  onRemove,
  profileLabel,
  onProfile,
  testId,
}: {
  label: string;
  removeLabel: string;
  onRemove: () => void;
  /** Optional "Edit profile" item (operational AgentProfile — handoff:configure). */
  profileLabel?: string;
  onProfile?: () => void;
  testId: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
          data-testid={testId}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {onProfile && profileLabel ? (
          <DropdownMenuItem className="gap-2 text-[13px]" onClick={onProfile}>
            <Headset className="h-3.5 w-3.5" aria-hidden="true" />
            {profileLabel}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          className="gap-2 text-[13px] text-destructive focus:!bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] focus:!text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          {removeLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function InviteHelpdeskDialog({
  slug,
  open,
  onOpenChange,
  roleOptions,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  roleOptions: Array<{ value: string; label: string }>;
}) {
  const { t } = useTranslation("workspace");
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("helpdesk_member");

  const mutation = useMutation({
    mutationFn: () => inviteHelpdeskMember(slug, { email: email.trim(), role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: helpdeskTeamKeys.invitations(slug) });
      void qc.invalidateQueries({ queryKey: helpdeskTeamKeys.members(slug) });
      toast.success(t("helpdeskTeam.members.inviteToast", { email: email.trim() }));
      onOpenChange(false);
      setEmail("");
      setRole("helpdesk_member");
    },
    onError: (err) =>
      toast.error(t("helpdeskTeam.members.inviteFailed"), { description: formatApiError(err) }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="helpdesk-invite-dialog"
        className="max-w-md gap-0 rounded-2xl p-0"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) mutation.mutate();
          }}
        >
          <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
            <DialogTitle className="text-base font-semibold">
              {t("helpdeskTeam.members.inviteTitle")}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground">
              {t("helpdeskTeam.members.inviteSubtitle")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 px-5 pb-2">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium text-foreground">
                {t("helpdeskTeam.members.emailLabel")}
              </Label>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("helpdeskTeam.members.emailPlaceholder")}
                data-testid="helpdesk-invite-email"
                className="h-9 rounded-lg text-[13px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium text-foreground">
                {t("helpdeskTeam.members.roleLabel")}
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger
                  data-testid="helpdesk-invite-role"
                  className="h-9 rounded-lg text-[13px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
              className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              {t("helpdeskTeam.members.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={mutation.isPending || !email.trim()}
              data-testid="helpdesk-invite-submit"
              className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Mail className="h-4 w-4" aria-hidden="true" />
              )}
              {t("helpdeskTeam.members.sendInvite")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  mutationFn,
  invalidateKey,
  successMsg,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  mutationFn: () => Promise<unknown>;
  invalidateKey: readonly unknown[];
  successMsg: string;
}) {
  const { t } = useTranslation("workspace");
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invalidateKey });
      toast.success(successMsg);
      onOpenChange(false);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 rounded-2xl p-0">
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
            className="h-8 flex-1 rounded-md text-[13px] hover:bg-secondary-hover"
          >
            {t("helpdeskTeam.members.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="h-8 flex-1 gap-1.5 rounded-md text-[13px]"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
